import {
  createAdminWorkspaceTab,
  createProjectWorkspaceTab,
  normalizeWorkspacePath,
  sanitizeTabMemory,
  type TabMemory,
  type TabSearchParams,
  type WorkspaceTab,
} from "./locations";

export const TABS_STORAGE_KEY = "df-tabs:v4";
export const LEGACY_TABS_STORAGE_KEY = "df-tabs:v3";

function normalizeWorkspaceTab(tab: WorkspaceTab): WorkspaceTab {
  return {
    ...tab,
    currentPath: normalizeWorkspacePath(tab.currentPath),
    memory: sanitizeTabMemory(tab.memory),
  };
}

function parseSearchParams(item: Record<string, unknown>): TabSearchParams {
  return item.searchParams &&
    typeof item.searchParams === "object" &&
    !Array.isArray(item.searchParams)
    ? (item.searchParams as TabSearchParams)
    : {};
}

function parseMemory(item: Record<string, unknown>): TabMemory {
  return item.memory && typeof item.memory === "object" && !Array.isArray(item.memory)
    ? sanitizeTabMemory(item.memory as TabMemory)
    : {};
}

function migrateLegacyProjectMemory(searchParams: TabSearchParams, memory: TabMemory) {
  const migratedSearchParams: TabSearchParams = { ...searchParams };
  const migratedMemory: TabMemory = { ...memory };

  if (typeof searchParams.ep === "string" && searchParams.ep.length > 0) {
    migratedMemory.entityPath = searchParams.ep;
    delete migratedSearchParams.ep;
  }
  if (typeof searchParams.ei === "string" && searchParams.ei.length > 0) {
    migratedMemory.entityIconKey = searchParams.ei;
    delete migratedSearchParams.ei;
  }
  if (typeof searchParams.ap === "string" && searchParams.ap.length > 0) {
    migratedMemory.assistantPath = searchParams.ap;
    delete migratedSearchParams.ap;
  }

  return { searchParams: migratedSearchParams, memory: migratedMemory };
}

export function parseStoredTabs(
  stored: string | null,
  options: { migrateLegacyMemory?: boolean } = {}
): WorkspaceTab[] | null {
  if (!stored) return null;

  const parsed = JSON.parse(stored);
  if (!Array.isArray(parsed)) return null;

  const tabs: WorkspaceTab[] = [];
  for (const item of parsed) {
    if (!item || typeof item !== "object") continue;

    const kind = "kind" in item ? item.kind : null;
    const name = "name" in item && typeof item.name === "string" ? item.name : null;
    const currentPath =
      "currentPath" in item && typeof item.currentPath === "string" ? item.currentPath : null;
    const searchParams = parseSearchParams(item as Record<string, unknown>);
    const memory = parseMemory(item as Record<string, unknown>);

    if (kind === "project") {
      const tabId = "tabId" in item && typeof item.tabId === "string" ? item.tabId : null;
      const resourceId =
        "resourceId" in item && typeof item.resourceId === "string" ? item.resourceId : null;
      if (!tabId || !resourceId || !name || !currentPath) {
        continue;
      }

      const parsedProjectState = options.migrateLegacyMemory
        ? migrateLegacyProjectMemory(searchParams, memory)
        : { searchParams, memory };

      tabs.push(
        normalizeWorkspaceTab(
          createProjectWorkspaceTab({
            resourceId,
            name,
            currentPath,
            searchParams: parsedProjectState.searchParams,
            memory: parsedProjectState.memory,
          })
        )
      );
      continue;
    }

    if (kind === "admin") {
      tabs.push(
        normalizeWorkspaceTab(
          createAdminWorkspaceTab({
            currentPath: currentPath ?? undefined,
            searchParams,
            memory,
          })
        )
      );
    }
  }

  return tabs;
}

export function loadStoredTabs(): WorkspaceTab[] {
  try {
    const currentTabs = parseStoredTabs(sessionStorage.getItem(TABS_STORAGE_KEY));
    if (currentTabs) {
      return currentTabs;
    }

    const legacyTabs = parseStoredTabs(sessionStorage.getItem(LEGACY_TABS_STORAGE_KEY), {
      migrateLegacyMemory: true,
    });
    if (!legacyTabs) {
      return [];
    }

    saveTabsToStorage(legacyTabs);
    sessionStorage.removeItem(LEGACY_TABS_STORAGE_KEY);
    return legacyTabs;
  } catch {
    return [];
  }
}

export function saveTabsToStorage(tabs: WorkspaceTab[]) {
  const normalizedTabs = tabs.map((tab) => ({
    ...tab,
    currentPath: normalizeWorkspacePath(tab.currentPath),
    memory: sanitizeTabMemory(tab.memory),
  }));
  sessionStorage.setItem(TABS_STORAGE_KEY, JSON.stringify(normalizedTabs));
}
